import Service from '@/features/data/dataService.js'
import { API_CONFIG } from '@/shared/configs/apiConfig.js'

const REFRESH_INTERVAL = 60000 // 1 minute

let refreshInterval = null
let isRefreshing = false
let refreshPromise = null // Stocker la promesse du refresh en cours

/**
 * Démarre le rafraîchissement périodique des soldes
 */
export function startBalancesRefresh() {
  if (refreshInterval) return // Déjà démarré

  // Premier appel immédiat
  refreshBalances()

  // Puis périodiquement
  refreshInterval = setInterval(() => {
    refreshBalances()
  }, REFRESH_INTERVAL)
}

/**
 * Arrête le rafraîchissement périodique
 */
export function stopBalancesRefresh() {
  if (refreshInterval) {
    clearInterval(refreshInterval)
    refreshInterval = null
  }
}

/**
 * Rafraîchit les soldes (appel manuel ou automatique)
 * @param {boolean} force - Force le rafraîchissement même si un est en cours
 */
export async function refreshBalances(force = false) {
  // Si un rafraîchissement est déjà en cours et pas de force, attendre qu'il se termine
  if (isRefreshing && !force) {
    console.log('⏳ Balances refresh already in progress, waiting...')
    return refreshPromise // Retourner la promesse du refresh en cours
  }

  isRefreshing = true
  const startTime = Date.now()

  // Créer une promesse qui sera résolue quand le refresh se termine
  refreshPromise = (async () => {
    try {
      console.log('🔄 Refreshing balances...')

      // Récupérer les données via le fetch balances
      const result = await Service.getSingleData('balances', {})

      if (result?.data) {
        // Mettre à jour le cache global
        if (typeof window !== 'undefined') {
          if (!window.__RAPIDOO_GLOBAL_DATA__) {
            window.__RAPIDOO_GLOBAL_DATA__ = {}
          }

          // Préserver les soldes individuels existants (déjà peuplés par balances.js)
          const existingBalances = window.__RAPIDOO_GLOBAL_DATA__.balances

          window.__RAPIDOO_GLOBAL_DATA__.balances = {
            customers: existingBalances?.customers || {},
            riders: existingBalances?.riders || {},
            restaurants: existingBalances?.restaurants || {},
            lastUpdate: Date.now(),
            // Garder les agrégats pour compatibilité
            customerBalance: result.data.customerBalance || 0,
            riderBalance: result.data.riderBalance || 0,
            restaurantBalance: result.data.restaurantBalance || 0,
            totalBalance: result.data.totalBalance || 0,
          }
        }

        const elapsed = Date.now() - startTime
        console.log(`✅ Balances refreshed in ${elapsed}ms`, {
          customerBalance: result.data.customerBalance,
          riderBalance: result.data.riderBalance,
          restaurantBalance: result.data.restaurantBalance,
        })
      }
    } catch (error) {
      console.error('❌ Failed to refresh balances:', error)
    } finally {
      isRefreshing = false
    }
  })()

  return refreshPromise
}

/**
 * Récupère le solde d'un utilisateur par type et ID
 * @param {string} type - 'customer' | 'rider' | 'restaurant'
 * @param {string} userId - L'ID de l'utilisateur
 * @returns {number|null} - Le solde ou null si non disponible
 */
export function getUserBalance(type, userId) {
  if (typeof window === 'undefined' || !window.__RAPIDOO_GLOBAL_DATA__?.balances) {
    return null
  }

  const cache = window.__RAPIDOO_GLOBAL_DATA__.balances
  const typeKey = `${type}s` // customers, riders, restaurants

  return cache[typeKey]?.[userId] ?? null
}

/**
 * Récupère tous les soldes en cache
 * @returns {object|null}
 */
export function getAllBalances() {
  if (typeof window === 'undefined') {
    return null
  }
  return window.__RAPIDOO_GLOBAL_DATA__?.balances || null
}

/**
 * Met à jour le solde d'un utilisateur spécifique dans le cache
 * @param {string} type - 'customer' | 'rider' | 'restaurant'
 * @param {string} userId - L'ID de l'utilisateur
 * @param {number} balance - Le nouveau solde
 */
export function setUserBalance(type, userId, balance) {
  if (typeof window === 'undefined' || !window.__RAPIDOO_GLOBAL_DATA__) {
    return
  }

  if (!window.__RAPIDOO_GLOBAL_DATA__.balances) {
    window.__RAPIDOO_GLOBAL_DATA__.balances = {
      customers: {},
      riders: {},
      restaurants: {},
      lastUpdate: Date.now(),
    }
  }

  const typeKey = `${type}s`
  window.__RAPIDOO_GLOBAL_DATA__.balances[typeKey][userId] = balance
}

/**
 * Vérifie si le cache est obsolète (plus de 30 secondes)
 * @returns {boolean}
 */
export function isBalancesCacheStale() {
  if (typeof window === 'undefined') {
    return true
  }

  // Initialiser le cache des soldes s'il n'existe pas
  if (!window.__RAPIDOO_GLOBAL_DATA__) {
    window.__RAPIDOO_GLOBAL_DATA__ = {}
  }

  if (!window.__RAPIDOO_GLOBAL_DATA__.balances) {
    window.__RAPIDOO_GLOBAL_DATA__.balances = {
      customers: {},
      riders: {},
      restaurants: {},
      lastUpdate: null,
    }
    return true
  }

  const lastUpdate = window.__RAPIDOO_GLOBAL_DATA__.balances.lastUpdate
  if (!lastUpdate) return true

  const STALE_THRESHOLD = 30000 // 30 secondes
  return Date.now() - lastUpdate > STALE_THRESHOLD
}
